'use client';

import { JSX, useCallback, useState } from 'react';
import { t } from 'utils/locale';
import { Translated } from 'utils/staticText';
import { EmailTypoSuggestionStaticText } from './i18n';
import { getKnownEmailDomains, suggestEmailDomain } from './suggestEmailDomain';
import { useClientContext } from '$/modules/context/client';

export type EmailTypoSuggestionState = {
  suggestedEmail: string | null;
  /**
   * Call on blur. Takes the field's own format verdict so a suggestion never
   * shows next to the format error (CMS-1981).
   */
  check: (email: string, isFormatValid: boolean) => void;
  /** Call it on every edit so a stale suggestion disappears until the next check. */
  clear: () => void;
};

export function useEmailTypoSuggestion(): EmailTypoSuggestionState {
  const { brand } = useClientContext();
  const [suggestedEmail, setSuggestedEmail] = useState<string | null>(null);

  return {
    suggestedEmail,
    check: useCallback(
      (email: string, isFormatValid: boolean) =>
        setSuggestedEmail(
          isFormatValid
            ? suggestEmailDomain(email, getKnownEmailDomains(brand))
            : null,
        ),
      [brand],
    ),
    clear: useCallback(() => setSuggestedEmail(null), []),
  };
}

/** "Did you mean …?" caption under an email input with a tappable address. */
export function EmailTypoSuggestion({
  onAccept,
  staticText,
  suggestion,
}: {
  onAccept: (email: string) => void;
  staticText: Translated<typeof EmailTypoSuggestionStaticText>;
  suggestion: EmailTypoSuggestionState;
}): JSX.Element | null {
  const { suggestedEmail } = suggestion;
  if (!suggestedEmail) return null;
  return (
    <div
      data-component="email-typo-suggestion"
      // eslint-disable-next-line tailwindcss/no-arbitrary-value
      className="px-padding/x-sm pt-1 text-[#b98b33] alpha-caption"
    >
      {t(staticText.emailTypoSuggestion, {
        suggestedEmail: (
          <button
            key="suggested-email"
            type="button"
            className="underline"
            onClick={() => {
              onAccept(suggestedEmail);
              suggestion.clear();
            }}
          >
            {suggestedEmail}
          </button>
        ),
      })}
    </div>
  );
}
